import React, { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useKindeAuth } from '@kinde-oss/kinde-auth-react';
import { Button } from '@/shared/components/ui/button';
import { CareConciergeFormData, PageOrigin } from './types';
import RequestorTypeSelector from './RequestorTypeSelector';
import CareSeekerWorkflow from './CareSeekerWorkflow';
import FinancialAdvisorWorkflow from './FinancialAdvisorWorkflow';
import { StatusMessages } from './StatusMessages';
import { submitCareConciergeRequest } from '../../api/contactApi';
import { useAuth } from '@/features/authentication/hooks/useAuth';

import { ServiceCoordinationCallButton } from '@/shared/components/ServiceCoordinationCallButton';
import { useRecaptchaGuestSession } from '@/features/guest-session/hooks/useRecaptchaGuestSession';
import { submitGuestCareConciergeRequest } from '../../api/contactApi';

type CareConciergContactFormProps = {
  sourceContext?: 'provider-search' | 'navigator' | 'navigator-exhausted';
  pageOrigin: PageOrigin;
  initialCategories?: string[];
  className?: string;
  embedded?: boolean;
  /** Feature 104 (FP shareable page): lock to the FA workflow regardless of role. */
  forceFinancialAdvisorWorkflow?: boolean;
  /** Feature 104 (FP shareable page): allow logged-out submission via the guest path. */
  allowGuestSubmission?: boolean;
};

const CareConciergContactForm: React.FC<CareConciergContactFormProps> = ({
  sourceContext: _sourceContext = 'provider-search',
  pageOrigin,
  initialCategories,
  className = '',
  embedded = false,
  forceFinancialAdvisorWorkflow = false,
  allowGuestSubmission = false,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { isAuthenticated, login } = useKindeAuth();
  const { user } = useAuth();
  const { recaptchaElement, ensureGuestSession } = useRecaptchaGuestSession();
  const isFA = user?.role === 'FINANCIAL_ADVISOR' || forceFinancialAdvisorWorkflow;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [requestId, setRequestId] = useState<string>('');

  const fullName = [user?.firstName, user?.lastName].filter(Boolean).join(' ');

  const defaultFormValues: Partial<CareConciergeFormData> = {
    ...(fullName ? { requestorName: fullName } : {}),
    ...(user?.email ? { requestorEmail: user.email } : {}),
    ...(isFA ? { requestorType: 'FINANCIAL_ADVISOR' } : {}),
    ...(initialCategories?.length ? { categories: initialCategories } : {}),
  };

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
    reset,
  } = useForm<CareConciergeFormData>({
    defaultValues: Object.keys(defaultFormValues).length > 0 ? defaultFormValues : undefined,
  });

  const requestorType = watch('requestorType');

  useEffect(() => {
    if (isFA && requestorType !== 'FINANCIAL_ADVISOR') {
      setValue('requestorType', 'FINANCIAL_ADVISOR');
    }
  }, [isFA, requestorType, setValue]);
  const urgencyLevel = watch('urgencyLevel');
  const relationshipToRecipient = watch('relationshipToRecipient');
  const needsSameLocation = watch('needsSameLocation');
  const clientPermissionConfirmed = watch('clientPermissionConfirmed');
  const advisorStayLooped = watch('advisorStayLooped');

  const onSubmit = async (data: CareConciergeFormData) => {
    setIsSubmitting(true);
    setSubmitStatus('idle');
    setErrorMessage('');

    try {
      const categoriesRaw = data.categories as string[] | string | undefined;
      const categoriesArray = Array.isArray(categoriesRaw)
        ? categoriesRaw
        : typeof categoriesRaw === 'string' && categoriesRaw.trim()
          ? categoriesRaw.split(',').map((s) => s.trim()).filter(Boolean)
          : undefined;

      const requestData = {
        ...data,
        pageOrigin,
        categories: categoriesArray,
      };
      // Feature 104: the guest endpoint accepts the FA workflow; guests are
      // authorized by a guest-session token minted behind reCAPTCHA.
      await ensureGuestSession(isAuthenticated);
      const response = isAuthenticated
        ? await submitCareConciergeRequest(requestData)
        : await submitGuestCareConciergeRequest(requestData);
      setRequestId(response.id);
      setSubmitStatus('success');
      reset();
      containerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } catch (error: any) {
      console.error('Failed to submit care concierge request:', error);
      setSubmitStatus('error');
      setErrorMessage(error.message || 'Failed to submit request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Show login prompt if not authenticated (Feature 104: unless the surface
  // explicitly supports guest submission, e.g. the FP shareable page)
  if (!isAuthenticated && !allowGuestSubmission) {
    return (
      <div
        className={
          embedded
            ? `text-center ${className}`
            : `mt-8 bg-white border border-[#E5E7EB] rounded-2xl p-8 text-center ${className}`
        }
      >
        {!embedded && (
          <>
            <h3 className="text-2xl font-recoleta font-semibold text-[#111827] mb-4">
              Can't find what you're looking for?
            </h3>
            <p className="text-[#4B5563] mb-4">
              Our care concierge team can help! Log in to submit a request, or schedule a free
              15-minute service coordination call.
            </p>
          </>
        )}
        <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
          <Button
            type="button"
            onClick={() => login()}
            className="inline-flex items-center px-6 py-3 text-base font-termina font-medium text-white bg-[#026857] hover:bg-[#025246] rounded-md transition-colors"
          >
            Log in to submit request
          </Button>
          <ServiceCoordinationCallButton className="border-[#026857] text-[#026857] hover:bg-[#026857]/10" />
        </div>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={
        embedded
          ? className
          : `mt-8 bg-white border-2 border-[#026857]/20 rounded-2xl shadow-sm ${className}`
      }
    >
      <div className={embedded ? '' : 'p-6 md:p-8'}>
        {!embedded && (
          <div className="mb-6 text-center">
            <h3 className="text-2xl md:text-3xl font-recoleta font-semibold text-[#111827] mb-2">
              Can't find what you're looking for? We can help!
            </h3>
            <p className="text-[#4B5563] font-termina mb-4">
              Our team is happy to help you find the right service provider. Feel free to reach out at any time—we're here to support you every step of the way.
            </p>
            {/* Feature 137: advisors get the call as their main path, the form as the fallback;
                care seekers keep the form first and the call low-key. */}
            <p className="text-[#4B5563] font-termina mb-4">
              {isFA
                ? 'The quickest way to get your client the right help is a free 15-minute service coordination call with our team. Prefer to write it down? Fill out the form below.'
                : "If you'd like someone from our team to reach out to you, fill out the form below. Or schedule a free 15-minute service coordination call with our team."}
            </p>
            <ServiceCoordinationCallButton
              variant={isFA ? 'default' : 'outline'}
              className={
                isFA
                  ? 'bg-[#026857] text-white hover:bg-[#025246]'
                  : 'border-[#026857] text-[#026857] hover:bg-[#026857]/10'
              }
            />
          </div>
        )}

        <StatusMessages
          submitStatus={submitStatus}
          requestId={requestId}
          errorMessage={errorMessage}
          onDismiss={() => setSubmitStatus('idle')}
        />

        {recaptchaElement}
        {submitStatus !== 'success' && (
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
            {/* Requestor Type Selector */}
            {isFA ? (
              <RequestorTypeSelector
                register={register}
                errors={errors}
                selectedType="FINANCIAL_ADVISOR"
              />
            ) : (
              <RequestorTypeSelector
                register={register}
                errors={errors}
                selectedType={requestorType}
                showFinancialAdvisorOption={false}
              />
            )}

            {/* Conditional Workflow Rendering */}
            {requestorType && requestorType !== 'FINANCIAL_ADVISOR' && (
              <CareSeekerWorkflow
                register={register}
                errors={errors}
                urgencyLevel={urgencyLevel}
                relationshipToRecipient={relationshipToRecipient}
                needsSameLocation={needsSameLocation}
                requestorType={requestorType}
              />
            )}

            {requestorType === 'FINANCIAL_ADVISOR' && (
              <FinancialAdvisorWorkflow
                register={register}
                errors={errors}
                urgencyLevel={urgencyLevel}
                advisorStayLooped={advisorStayLooped}
              />
            )}

            {/* Submit Button */}
            {requestorType && (
              <div className="pt-4 border-t border-[#E5E7EB]">
                <button
                  type="submit"
                  disabled={
                    isSubmitting ||
                    (requestorType === 'FINANCIAL_ADVISOR' && !clientPermissionConfirmed)
                  }
                  className="w-full md:w-auto px-8 py-3 text-base font-termina font-medium text-white bg-[#026857] hover:bg-[#025246] disabled:bg-[#6B7280] disabled:cursor-not-allowed rounded-md transition-colors"
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg
                        className="animate-spin h-5 w-5"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Submitting...
                    </span>
                  ) : (
                    'Submit Request'
                  )}
                </button>
                {requestorType === 'FINANCIAL_ADVISOR' && !clientPermissionConfirmed && (
                  <p className="mt-2 text-sm text-red-600 font-termina">
                    You must confirm client permission before submitting
                  </p>
                )}
              </div>
            )}
          </form>
        )}
      </div>
    </div>
  );
};

export default CareConciergContactForm;
